import type { ComputedRef, Ref } from 'vue'
import { isNavigationFailure, type Router } from 'vue-router'
import type { FormInstance } from 'element-plus'

import { projectApi } from '@/api'
import { taskSquareApi } from '@/api/taskSquare'
import { getContentSourceLabel, getProjectModeLabel } from '@/utils/projectModes'
import { buildGenerationRoute, queueGenerationAction } from '@/utils/workflowGeneration'

import type { SubmitRequirementFormData } from './useSubmitRequirementPageState'

interface ContentMeta {
  source: string
  libraryCount: number
  updatedAt: string
  fromLibrary: boolean
}

interface TaskPublishForm {
  enabled: boolean
  title: string
  summary: string
  allowMultipleClaims: boolean
}

interface StartRequest {
  project_id: number
  stage?: 'script'
  input_data: Record<string, any>
  require_proposal_approval: boolean
  text_model_id?: string
}

interface UseSubmitRequirementSubmissionOptions {
  router: Router
  formRef: Ref<FormInstance | undefined>
  submitting: Ref<boolean>
  createdProjectId: Ref<number | null>
  formData: SubmitRequirementFormData
  contentCreationMode: Ref<'direct' | 'embed'>
  modeSelected: ComputedRef<boolean>
  isContentDriven: ComputedRef<boolean>
  isScriptImport: ComputedRef<boolean>
  requiresContentEmbedding: ComputedRef<boolean>
  resolvedContentSubmission: ComputedRef<string>
  shouldShowTaskPublishPanel: ComputedRef<boolean>
  hasEmbeddedContent: Ref<boolean>
  contentConfirmed: Ref<boolean>
  sourceContent: Ref<string>
  embeddedContent: Ref<string>
  contentMeta: ContentMeta
  scriptImportSource: Ref<string>
  scriptImportSourceLabel: ComputedRef<string>
  proposalTextModelId: Ref<string>
  taskPublishForm: TaskPublishForm
  addProject: (project: any) => void
  success: (message: string) => void
  error: (message: string) => void
  warning: (message: string) => void
}

export function useSubmitRequirementSubmission(options: UseSubmitRequirementSubmissionOptions) {
  const buildProductDrivenDescription = () => {
    const sections = [
      `项目模式：${getProjectModeLabel(options.formData.projectMode)}`,
      `目标用户：${options.formData.targetAudience}`,
      `核心需求：${options.formData.requirementText}`,
      options.formData.visualStyle ? `视觉风格：${options.formData.visualStyle}` : '',
      options.formData.referenceUrl ? `参考素材：${options.formData.referenceUrl}` : '',
      options.formData.remarks ? `补充说明：${options.formData.remarks}` : '',
    ].filter(Boolean)

    return sections.join('\n\n')
  }

  const buildContentDrivenDescription = () => {
    const sections = [
      `项目模式：${getProjectModeLabel(options.formData.projectMode)}`,
      `内容进入方式：${options.requiresContentEmbedding.value ? '整合后进入项目' : '直接使用当前内容'}`,
      `内容类型：${options.formData.contentType}`,
      options.sourceContent.value ? `原始素材：${options.sourceContent.value}` : '',
      `当前内容：${options.formData.contentBody}`,
      options.embeddedContent.value.trim() ? `整合后内容：${options.embeddedContent.value}` : '',
      `产品名称：${options.formData.productName}`,
      `产品特点/卖点：${options.formData.productFeatures}`,
      options.formData.productScene ? `产品使用场景：${options.formData.productScene}` : '',
      options.formData.remarks ? `补充说明：${options.formData.remarks}` : '',
      options.contentMeta.source ? `生成来源：${getContentSourceLabel(options.contentMeta.source)}` : '',
      options.contentConfirmed.value ? '素材库状态：已确认收录' : '素材库状态：未收录',
    ].filter(Boolean)

    return sections.join('\n\n')
  }

  const buildScriptImportDescription = () => {
    const sections = [
      `项目模式：${getProjectModeLabel(options.formData.projectMode)}`,
      options.formData.scriptTitle.trim() ? `剧本标题：${options.formData.scriptTitle.trim()}` : '',
      `剧本导入来源：${options.scriptImportSourceLabel.value}`,
      options.formData.targetAudience.trim() ? `目标用户：${options.formData.targetAudience.trim()}` : '',
      options.formData.visualStyle.trim() ? `视觉风格：${options.formData.visualStyle.trim()}` : '',
      options.formData.remarks.trim() ? `补充说明：${options.formData.remarks.trim()}` : '',
      `导入剧本：${options.formData.scriptBody.trim()}`,
    ].filter(Boolean)

    return sections.join('\n\n')
  }

  const buildProductDrivenStartRequest = async (projectId: number): Promise<StartRequest> => {
    return {
      project_id: projectId,
      input_data: {
        raw_requirement: options.formData.requirementText,
        target_audience: options.formData.targetAudience,
        visual_style: options.formData.visualStyle,
        reference_url: options.formData.referenceUrl,
        notes: options.formData.remarks,
        project_mode: 'product_driven',
        project_type: options.formData.productScenario,
        target_duration: options.formData.duration,
      },
      require_proposal_approval: true,
      text_model_id: options.proposalTextModelId.value || undefined,
    }
  }

  const buildContentDrivenStartRequest = (projectId: number): StartRequest => {
    const finalContent = options.resolvedContentSubmission.value
    return {
      project_id: projectId,
      input_data: {
        raw_requirement: finalContent,
        project_mode: 'content_driven',
        project_type: 'content_driven',
        content_creation_mode: options.contentCreationMode.value,
        content_type: options.formData.contentType,
        source_content: options.sourceContent.value.trim() || options.formData.contentBody.trim() || finalContent,
        edited_content: options.formData.contentBody.trim() || finalContent,
        final_embedded_content: finalContent,
        product_name: options.formData.productName.trim(),
        product_features: options.formData.productFeatures.trim(),
        product_scene: options.formData.productScene.trim(),
        target_duration: options.formData.duration,
        visual_style: options.formData.visualStyle.trim(),
        notes: options.formData.remarks.trim(),
        content_generation_source: options.contentMeta.source,
      },
      require_proposal_approval: true,
      text_model_id: options.proposalTextModelId.value || undefined,
    }
  }

  const buildScriptImportStartRequest = (projectId: number): StartRequest => {
    const scriptBody = options.formData.scriptBody.trim()
    return {
      project_id: projectId,
      stage: 'script',
      input_data: {
        raw_requirement: scriptBody,
        project_mode: 'script_import',
        project_type: 'script_import',
        script_title: options.formData.scriptTitle.trim() || options.formData.projectName.trim(),
        imported_script_content: scriptBody,
        script_import_source: options.scriptImportSource.value,
        target_duration: options.formData.duration,
        target_audience: options.formData.targetAudience.trim(),
        visual_style: options.formData.visualStyle.trim(),
        notes: options.formData.remarks.trim(),
      },
      require_proposal_approval: false,
    }
  }

  const navigateToWorkflow = async (projectId: number | string, action?: { stage?: 'proposal' | 'script' }) => {
    const workflowPath = buildGenerationRoute(projectId, action)
    try {
      await options.router.push(workflowPath)
    } catch (error) {
      if (!isNavigationFailure(error) && typeof window !== 'undefined') {
        window.location.assign(workflowPath)
        return
      }

      if (typeof window !== 'undefined') {
        window.location.assign(workflowPath)
      }
    }
  }

  const handleSubmit = async () => {
    if (!options.modeSelected.value || !options.formRef.value) {
      options.warning('先选择项目类型')
      return
    }

    const valid = await options.formRef.value.validate().catch(() => false)
    if (!valid) {
      options.error('还有必填内容没填写完整')
      return
    }

    if (options.isContentDriven.value) {
      if (!options.formData.contentBody.trim()) {
        options.error('请先生成或填写内容')
        return
      }
      if (options.requiresContentEmbedding.value && (!options.formData.productName.trim() || !options.formData.productFeatures.trim())) {
        options.error('如果要做内容整合，请先把产品名称和卖点补充完整')
        return
      }
      if (options.requiresContentEmbedding.value && !options.hasEmbeddedContent.value) {
        options.error('请先把产品信息融入内容，再继续创建项目')
        return
      }
    }

    if (options.isScriptImport.value && !options.formData.scriptBody.trim()) {
      options.error('请先粘贴或导入剧本正文')
      return
    }

    options.submitting.value = true
    try {
      const createdProject = await projectApi.create({
        title: options.formData.projectName,
        description: options.isScriptImport.value ? buildScriptImportDescription() : options.isContentDriven.value ? buildContentDrivenDescription() : buildProductDrivenDescription(),
        genre: options.isScriptImport.value ? 'script_import' : options.isContentDriven.value ? options.formData.contentType : options.formData.productScenario,
        target_duration: options.formData.duration,
        total_episodes: 1,
      })

      options.addProject(createdProject)
      options.createdProjectId.value = Number(createdProject.id)

      const startRequest = options.isScriptImport.value
        ? buildScriptImportStartRequest(Number(createdProject.id))
        : options.isContentDriven.value
          ? buildContentDrivenStartRequest(Number(createdProject.id))
          : await buildProductDrivenStartRequest(Number(createdProject.id))

      let publishedToTaskSquare = false

      if (options.shouldShowTaskPublishPanel.value && options.taskPublishForm.enabled) {
        try {
          await taskSquareApi.publish({
            source_project_id: Number(createdProject.id),
            title: options.taskPublishForm.title.trim() || options.formData.projectName.trim(),
            summary: options.taskPublishForm.summary.trim(),
            description: options.isContentDriven.value ? buildContentDrivenDescription() : buildProductDrivenDescription(),
            genre: options.isContentDriven.value ? options.formData.contentType : options.formData.productScenario,
            target_duration: Math.round(Number(options.formData.duration || 0) * 60) || undefined,
            intake_payload: {
              project_mode: options.formData.projectMode,
              target_audience: options.formData.targetAudience,
              requirement_text: options.formData.requirementText,
              visual_style: options.formData.visualStyle,
              remarks: options.formData.remarks,
              content_type: options.formData.contentType,
              product_name: options.formData.productName,
              product_features: options.formData.productFeatures,
              product_scene: options.formData.productScene,
            },
            start_request: startRequest,
            allow_multiple_claims: options.taskPublishForm.allowMultipleClaims,
          })
          publishedToTaskSquare = true
        } catch {
          options.warning('项目已创建，但同步发布到任务广场失败；你可以稍后去任务广场手动发布')
        }
      }

      if (options.shouldShowTaskPublishPanel.value && options.taskPublishForm.enabled) {
        options.success(publishedToTaskSquare ? '项目已创建并发布到任务广场' : '项目已创建，可前往管理后台手动发布任务')
        await options.router.push({ name: 'AdminPanel', query: { tab: publishedToTaskSquare ? 'task-square' : 'projects' } })
        return
      }

      queueGenerationAction(createdProject.id, {
        intent: 'start',
        stage: options.isScriptImport.value ? 'script' : 'proposal',
        startRequest,
      })

      options.success('项目已创建，正在进入项目流程')
      await navigateToWorkflow(createdProject.id, { stage: options.isScriptImport.value ? 'script' : 'proposal' })
    } catch (error: any) {
      const detail = error?.response?.data?.detail
      const existingProjectId = Number(detail?.existing_project_id)
      if (error?.response?.status === 409 && Number.isFinite(existingProjectId) && existingProjectId > 0) {
        options.createdProjectId.value = existingProjectId
        options.warning(detail?.message || '已存在同名进行中的项目，正在为你跳回原项目')
        await navigateToWorkflow(existingProjectId)
        return
      }

      const message = typeof detail === 'string'
        ? detail
        : error?.response?.data?.message || '创建项目失败，请稍后再试'
      options.error(message)
    } finally {
      options.submitting.value = false
    }
  }

  return {
    handleSubmit,
    navigateToWorkflow,
  }
}